import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Repeat, ShieldCheck, AlertTriangle, RotateCw, XCircle, Flame } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

const STATUS_VARIANT: Record<string, any> = {
  active: 'default', pending: 'secondary',
  cancelled: 'outline', expired: 'outline', failed: 'destructive',
};

export default function MySubscriptions() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['my-subscriptions', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('subscriptions' as any)
        .select('*, advertising_packages(name, tier_label, price_etb, duration_days)')
        .eq('user_id', user!.id)
        .order('expires_at', { ascending: false });
      return (data as any[]) || [];
    },
    enabled: !!user,
  });

  const { data: boosts } = useQuery({
    queryKey: ['my-subscription-boosts', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.rpc('subscription_boost_summary' as any, { _user_id: user!.id });
      const map: Record<string, any> = {};
      ((data as any[]) || []).forEach((row) => { map[row.subscription_id] = row; });
      return map;
    },
  });

  // Realtime sync — refetch immediately on subscription or invoice changes for this user
  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`my-billing-${user.id}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'subscriptions', filter: `user_id=eq.${user.id}` },
        () => {
          qc.invalidateQueries({ queryKey: ['my-subscriptions', user.id] });
          qc.invalidateQueries({ queryKey: ['my-invoices', user.id] });
        })
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'payment_invoices', filter: `user_id=eq.${user.id}` },
        (payload: any) => {
          qc.invalidateQueries({ queryKey: ['my-invoices', user.id] });
          qc.invalidateQueries({ queryKey: ['my-subscriptions', user.id] });
          if (payload.new?.status === 'paid' && payload.old?.status !== 'paid') {
            toast.success(`Payment confirmed — invoice ${payload.new.invoice_number}`);
          } else if (payload.new?.status === 'failed' && payload.old?.status !== 'failed') {
            toast.error(`Payment failed for invoice ${payload.new.invoice_number}`);
          }
        })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user, qc]);

  const cancel = async (id: string) => {
    const { error } = await supabase.rpc('cancel_subscription_renewal' as any, {
      _subscription_id: id, _reason: 'user_requested',
    });
    if (error) return toast.error(error.message);
    toast.success('Auto-renewal cancelled. Your current period stays active until it expires.');
    qc.invalidateQueries({ queryKey: ['my-subscriptions', user?.id] });
  };

  const reactivate = async (id: string) => {
    const { error } = await supabase.rpc('reactivate_subscription_renewal' as any, {
      _subscription_id: id,
    });
    if (error) return toast.error(error.message);
    toast.success('Auto-renewal re-enabled.');
    qc.invalidateQueries({ queryKey: ['my-subscriptions', user?.id] });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Repeat className="h-5 w-5 text-primary" /> My Subscriptions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <Skeleton className="h-32" />
        ) : !data?.length ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No active subscriptions.</p>
        ) : (
          data.map((s) => {
            const expiresAt = new Date(s.expires_at);
            const isExpired = expiresAt < new Date();
            return (
              <div key={s.id} className="rounded-lg border p-4 flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1.5 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold">{s.advertising_packages?.name || 'Package'}</p>
                    {s.advertising_packages?.tier_label && (
                      <Badge variant="outline" className="text-[10px]">{s.advertising_packages.tier_label}</Badge>
                    )}
                    <Badge variant={STATUS_VARIANT[s.status] || 'secondary'} className="capitalize">{s.status}</Badge>
                    {s.auto_renew ? (
                      <Badge variant="default" className="text-[10px] gap-1"><ShieldCheck className="h-3 w-3" /> Auto-renew on</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">Auto-renew off</Badge>
                    )}
                    {(s.payment_failure_count ?? 0) > 0 && (
                      <Badge variant="destructive" className="text-[10px] gap-1">
                        <AlertTriangle className="h-3 w-3" /> {s.payment_failure_count} failed payments
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {isExpired ? 'Expired ' : 'Renews / expires '}
                    {format(expiresAt, 'd MMM yyyy')} ({formatDistanceToNow(expiresAt, { addSuffix: true })})
                  </p>
                  {s.cancelled_at && (
                    <p className="text-xs text-muted-foreground">
                      Cancelled {format(new Date(s.cancelled_at), 'd MMM yyyy')} — keeps access until expiry.
                    </p>
                  )}
                  {boosts?.[s.id] && (
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <Badge variant="default" className="gap-1 capitalize">
                        <Flame className="h-3 w-3" />
                        Search boost: {String(boosts[s.id].tier).replace('_', ' ')}
                      </Badge>
                      <span className="text-muted-foreground tabular-nums">
                        {boosts[s.id].used} / {boosts[s.id].quota} slots used
                      </span>
                      <Link to="/dashboard/listings" className="text-primary font-medium hover:underline">
                        Manage boosted listings →
                      </Link>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {s.auto_renew && s.status === 'active' && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-1">
                          <XCircle className="h-3.5 w-3.5" /> Cancel renewal
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel auto-renewal?</AlertDialogTitle>
                          <AlertDialogDescription>
                            You'll keep full access to <strong>{s.advertising_packages?.name}</strong> until{' '}
                            <strong>{format(expiresAt, 'd MMM yyyy')}</strong>. No new invoices will be generated and
                            any pending renewal invoices will be cancelled.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Keep auto-renewal</AlertDialogCancel>
                          <AlertDialogAction onClick={() => cancel(s.id)}>Yes, cancel renewal</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  {!s.auto_renew && !isExpired && (
                    <Button variant="default" size="sm" className="gap-1" onClick={() => reactivate(s.id)}>
                      <RotateCw className="h-3.5 w-3.5" /> Resume auto-renewal
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
